import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import bgVideo from "../assets/lbg.mp4";

const money = (value) =>
  `PHP ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function AdminReminderCenter() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dispatching, setDispatching] = useState(false);
  const [toast, setToast] = useState(null);
  const [data, setData] = useState({
    generated_at: null,
    summary: {},
    rent_due: [],
    lease_expiry: [],
    report_followups: [],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/operations/reminders/preview");
      setData({
        generated_at: res.data?.generated_at || null,
        summary: res.data?.summary || {},
        rent_due: Array.isArray(res.data?.rent_due) ? res.data.rent_due : [],
        lease_expiry: Array.isArray(res.data?.lease_expiry) ? res.data.lease_expiry : [],
        report_followups: Array.isArray(res.data?.report_followups)
          ? res.data.report_followups
          : [],
      });
    } catch (error) {
      console.error(error);
      setData({
        generated_at: null,
        summary: {},
        rent_due: [],
        lease_expiry: [],
        report_followups: [],
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(timer);
  }, [toast]);

  const dispatchReminders = async () => {
    try {
      setDispatching(true);
      const res = await api.post("/admin/operations/reminders/dispatch");
      setToast({ type: "success", message: res.data?.message || "Reminder batch sent." });
      await load();
    } catch (error) {
      console.error(error);
      setToast({ type: "error", message: "Failed to dispatch reminders." });
    } finally {
      setDispatching(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 font-sans text-white">
      <div className="absolute inset-0">
        <video data-workspace-background="true" aria-hidden="true" src={bgVideo} autoPlay muted loop playsInline className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/85" />
      </div>

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 border-b border-white/10 pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
              Automation
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-wide md:text-4xl">
              Reminder Center
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
              Preview due reminders for rent, lease expiry, and report follow-ups.
            </p>
            <p className="mt-2 text-xs uppercase tracking-[0.18em] text-slate-500">
              Last generated: {data.generated_at ? new Date(data.generated_at).toLocaleString() : "-"}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={load}
              className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-white/10"
            >
              Refresh Preview
            </button>
            <button
              onClick={dispatchReminders}
              disabled={dispatching}
              className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {dispatching ? "Dispatching..." : "Run Reminder Dispatch"}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="rounded-lg border border-white/10 bg-slate-950/90 py-10 text-center text-slate-300">
            Loading reminder preview...
          </div>
        ) : (
          <>
            <div className="grid gap-4 xl:grid-cols-3">
              <section className="rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl">
                <div className="mb-4 border-b border-white/10 pb-3">
                  <h2 className="text-xl font-bold text-white">Rent Due</h2>
                </div>
                <div className="space-y-3">
                  {data.rent_due.length === 0 ? (
                    <p className="text-sm text-slate-400">No rent reminders.</p>
                  ) : (
                    data.rent_due.slice(0, 12).map((item) => (
                      <article key={`${item.property_id}-${item.month_paid_for}`} className="rounded-lg border border-white/10 bg-white/5 p-3">
                        <p className="font-semibold text-white">{item.property_name}</p>
                        <p className="mt-1 text-xs text-slate-400">{item.tenant_name || "Unknown tenant"}</p>
                        <p className="mt-1 text-sm text-amber-200">{money(item.remaining_balance)}</p>
                        <button
                          onClick={() => navigate("/payments")}
                          className="mt-2 rounded-lg border border-white/15 px-3 py-1 text-xs font-semibold text-slate-200 hover:bg-white/10"
                        >
                          Open Payments
                        </button>
                      </article>
                    ))
                  )}
                </div>
              </section>

              <section className="rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl">
                <div className="mb-4 border-b border-white/10 pb-3">
                  <h2 className="text-xl font-bold text-white">Lease Expiry</h2>
                </div>
                <div className="space-y-3">
                  {data.lease_expiry.length === 0 ? (
                    <p className="text-sm text-slate-400">No lease expiry alerts.</p>
                  ) : (
                    data.lease_expiry.slice(0, 12).map((item) => (
                      <article key={`${item.property_id}-${item.lease_end_date}`} className="rounded-lg border border-white/10 bg-white/5 p-3">
                        <p className="font-semibold text-white">{item.property_name}</p>
                        <p className="mt-1 text-xs text-slate-400">{item.tenant_name || "Unassigned tenant"}</p>
                        <p className="mt-1 text-sm text-red-200">
                          {item.days_remaining < 0
                            ? `${Math.abs(item.days_remaining)} days overdue`
                            : `${item.days_remaining} days remaining`}
                        </p>
                        <button
                          onClick={() => navigate("/list-properties")}
                          className="mt-2 rounded-lg border border-white/15 px-3 py-1 text-xs font-semibold text-slate-200 hover:bg-white/10"
                        >
                          Open Properties
                        </button>
                      </article>
                    ))
                  )}
                </div>
              </section>

              <section className="rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl">
                <div className="mb-4 border-b border-white/10 pb-3">
                  <h2 className="text-xl font-bold text-white">Report Followups</h2>
                </div>
                <div className="space-y-3">
                  {data.report_followups.length === 0 ? (
                    <p className="text-sm text-slate-400">No report follow-up reminders.</p>
                  ) : (
                    data.report_followups.slice(0, 12).map((item) => (
                      <article key={item.report_id} className="rounded-lg border border-white/10 bg-white/5 p-3">
                        <p className="font-semibold text-white">{item.subject}</p>
                        <p className="mt-1 text-xs text-slate-400">{item.tenant_name || "Unknown tenant"}</p>
                        <p className="mt-1 text-xs text-slate-300">
                          {item.priority || "Normal"} / {String(item.sla_status || "on_track").replaceAll("_", " ")}
                        </p>
                        <button
                          onClick={() => navigate(`/admin/reports?focus=${item.report_id}&action=edit`)}
                          className="mt-2 rounded-lg border border-white/15 px-3 py-1 text-xs font-semibold text-slate-200 hover:bg-white/10"
                        >
                          Open Report
                        </button>
                      </article>
                    ))
                  )}
                </div>
              </section>
            </div>
          </>
        )}

        {toast && (
          <div
            className={`fixed right-4 top-4 z-50 rounded-lg px-5 py-3 text-sm font-semibold text-white shadow-2xl ${
              toast.type === "success" ? "bg-teal-600" : "bg-red-600"
            }`}
          >
            {toast.message}
          </div>
        )}
      </div>
    </div>
  );
}

